import React, { useState, useEffect } from "react";
import api from "../api";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../css/EnterpriseDetails.css";
import { formatDateToUserTimeZone } from "../utils/formatDate";
import { formatDateToUser } from "../utils/formatDate";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import editIcon from "../assets/Images/Edit.png";
import viewIcon from "../assets/Images/View.png";
import changeStatusIcon from "../assets/Images/ChangeStatus.png";
import crossIcon from "../assets/icons/crossIcon.png";
import addIcon from "../assets/Images/AddIcon.png";
import downloadIcon from "../assets/Images/Download.png";
import filterIcon from "../assets/Images/Filter.png";
import roleManagement from "../assets/icons/role-management.png"; // Replace with version icon if needed
import "../css/extra.css";

import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../utils/toastMessages";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { CustomDateInput } from "../utils/formatDate";
import { useTableSort } from "../hooks/useTableSort";
import { SortableHeader } from "../components/SortableHeader";

const BASE_URL = `${process.env.REACT_APP_API_URL}/version-history`;

const VersionHistory = () => {
  const [versions, setVersions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    version_number: "",
    version_module_name: "",
    version_changes: "",
    version_release_date: "",
  });
  const [editingVersionId, setEditingVersionId] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");
  const [dayFilter, setDayFilter] = useState("");
  const [viewMode, setViewMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [versionStatsList, setVersionStatsList] = useState({
    totalVersions: 0,
    activeVersions: 0,
    inactiveVersions: 0,
  });

  const statsLists = [
    { label: "Total Versions", key: "totalVersions" },
    { label: "Active Versions", key: "activeVersions" },
    { label: "Inactive Versions", key: "inactiveVersions" },
  ];

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  const handleReset = () => {
    setSortOrder("asc");
    setDayFilter("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleApply = () => {
    console.log("Sorting by:", sortOrder);
    console.log("Filtering by day:", dayFilter);
  };

  const fetchVersions = async () => {
    if (!token) {
      showErrorToast(TOAST_MESSAGES.UNAUTHORIZED);
      return;
    }
    try {
      const response = await api.get(BASE_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVersions(response.data);
    } catch (error) {
      console.error("Failed to fetch versions", error);
      //   showErrorToast(TOAST_MESSAGES.VERSION.FETCH_ERROR);
    }
  };

  useEffect(() => {
    fetchVersions();
  }, []);

  const filteredVersions = versions
    .filter((v) => {
      const lower = searchTerm.toLowerCase();
      const name = v.version_number ? v.version_number.toLowerCase() : "";
      const moduleName = v.version_module_name
        ? v.version_module_name.toLowerCase()
        : "";
      return name.includes(lower) || moduleName.includes(lower);
    })
    .filter((v) => {
      if (!dayFilter) return true;
      const date = new Date(v.version_release_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (dayFilter === "Today") return date >= today;
      if (dayFilter === "Yesterday") {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        return date >= yesterday && date < today;
      }
      return true;
    })
    .sort((a, b) => {
      const dateA = new Date(a.updated_timestamp);
      const dateB = new Date(b.updated_timestamp);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

  const totalEntries = filteredVersions.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentData = filteredVersions.slice(indexOfFirst, indexOfLast);

  const showingFrom = totalEntries === 0 ? 0 : indexOfFirst + 1;
  const showingTo = Math.min(indexOfLast, totalEntries);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const { sortedData, requestSort, sortConfig } = useTableSort(currentData);
  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageClick = (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this version?"))
      return;
    try {
      await api.delete(`${BASE_URL}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchVersions();
      if (currentData.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleEdit = (v) => {
    setFormData({
      version_number: v.version_number,
      version_module_name: v.version_module_name || "",
      version_changes: v.version_changes || "",
      version_release_date: formatDateToUserTimeZone(v.version_release_date),
    });
    setEditingVersionId(v.version_id);
    setViewMode(false);
    setShowForm(true);
  };

  const handleView = (v) => {
    setFormData({
      version_number: v.version_number,
      version_module_name: v.version_module_name || "",
      version_changes: v.version_changes || "",
      version_release_date: formatDateToUserTimeZone(v.version_release_date),
    });
    setEditingVersionId(v.version_id);
    setViewMode(true);
    setShowForm(true);
  };

  const handleChangeStatus = async (v) => {
    const currentStatus = v.version_status;
    const newStatus = currentStatus === "1" ? "0" : "1";
    const statusText = newStatus === "1" ? "Active" : "Inactive";

    try {
      await api.patch(
        `${BASE_URL}/${v.version_id}/status`,
        {status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      fetchVersions();
      showSuccessToast(
        `${TOAST_MESSAGES.VERSION.STATUS_CHANGE_SUCCESS} to ${statusText}`,
      );
    } catch (err) {
      console.error(err);
      showErrorToast(TOAST_MESSAGES.VERSION.STATUS_CHANGE_ERROR);
    }
  };

  useEffect(() => {
    const calculateStats = () => {
      const totalVersions = versions.length;
      const activeVersions = versions.filter(
        (v) => v.version_status === "1",
      ).length;
      const inactiveVersions = versions.filter(
        (v) => v.version_status === "0",
      ).length;
      setVersionStatsList({
        totalVersions,
        activeVersions,
        inactiveVersions,
      });
    };
    calculateStats();
  }, [versions]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const url = editingVersionId
        ? `${BASE_URL}/${editingVersionId}`
        : BASE_URL;
      const method = editingVersionId ? "PUT" : "POST";

      await api({
        method,
        url,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        data: formData,
      });

      setFormData({
        version_number: "",
        version_module_name: "",
        version_changes: "",
        version_release_date: "",
      });
      setShowForm(false);
      setEditingVersionId(null);
      fetchVersions();

      if (editingVersionId) {
        showSuccessToast(TOAST_MESSAGES.VERSION.UPDATE_SUCCESS);
      } else {
        showSuccessToast(TOAST_MESSAGES.VERSION.CREATE_SUCCESS);
      }
    } catch (err) {
      console.error(err);
      if (editingVersionId) {
        showErrorToast(TOAST_MESSAGES.VERSION.UPDATE_ERROR);
      } else {
        showErrorToast(TOAST_MESSAGES.VERSION.CREATE_ERROR);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setShowForm(false);
    setFormData({
      version_number: "",
      version_module_name: "",
      version_changes: "",
      version_release_date: "",
    });
    setEditingVersionId(null);
    setViewMode(false);
  };

  const exportExcel = () => {
    const wsData = [
      ["Version Number", "Module Name", "Changes", "Release Date", "Status"],
      ...filteredVersions.map((v) => [
        v.version_number,
        v.version_module_name || "",
        v.version_changes || "",
        formatDateToUserTimeZone(v.version_release_date),
        v.version_status === "1" ? "Active" : "Inactive",
      ]),
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "Versions");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, "version_history.xlsx");
  };

  return (
    <div className="mx-2 pt-1  h-100">
      <div className="d-flex flex-wrap gap-3 mb-0 align-items-stretch">
        {statsLists.map((item, index) => (
          <div
            key={index}
            className="dashboard-box p-3 rounded shadow-sm d-flex flex-column justify-content-between"
          >
            <div className="d-flex align-items-center mb-2">
              <img
                src={roleManagement}
                alt={item.label}
                style={{ width: "22px", height: "22px" }}
                className="icon-image1"
              />
              <h6 className="ms-2 mb-0">{item.label}</h6>
            </div>
            <h2 className="fw-bold">{versionStatsList[item.key]}</h2>
          </div>
        ))}
      </div>

      {/* Table & Controls */}
      <div className="d-flex refresh-prompts justify-content-between align-items-center mb-2 mt-1 ">
        <span className="fw-bold" style={{ fontSize: "20px" }}>
          Version History Details
        </span>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center entries-wrapper">
          <select
            className="form-select custom-select w-auto"
            value={entriesPerPage}
            onChange={handleEntriesChange}
          >
            {[50, 100, 500].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <label className="ms-2 mt-2 refresh-prompts">Entries per page</label>
        </div>

        <div className="d-flex align-items-center">
          <div className="custom-search-wrapper me-3">
            <div className="input-group custom-search-group">
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search Versions"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <button
            className="btn custom btn-outline-secondary me-2 action-button"
            onClick={exportExcel}
            title="Export to Excel"
          >
            <img src={downloadIcon} className="icon-image" alt="Download" />
          </button>

          <div className="">
            <button
              className="btn btn-outline-secondary action-button text-dark me-2"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              title="Filter"
            >
              <img src={filterIcon} className="icon-image" alt="Filter" />
            </button>

            <div
              className="dropdown-menu profile-menu-dropdown p-2"
              style={{ minWidth: "200px" }}
            >
              <h6 className="fw-bolder refresh-prompts px-0">Time</h6>
              <hr className="mt-0" />
              {["asc", "desc"].map((order) => (
                <div className="form-check" key={order}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="sortOrder"
                    id={order}
                    value={order}
                    checked={sortOrder === order}
                    onChange={() => setSortOrder(order)}
                  />
                  <label
                    className="form-check-label notification-label"
                    htmlFor={order}
                  >
                    {order === "asc" ? "Ascending" : "Descending"}
                  </label>
                </div>
              ))}

              <hr className="my-2" />
              <h6 className="fw-bolder refresh-prompts px-0">By Day</h6>
              <hr className="mt-0" />
              {["Today", "Yesterday", ""].map((day) => (
                <div className="form-check" key={day || "all"}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="dayFilter"
                    id={day || "all"}
                    value={day}
                    checked={dayFilter === day}
                    onChange={() => setDayFilter(day)}
                  />
                  <label
                    className="form-check-label fw-5 notification-label"
                    htmlFor={day || "all"}
                  >
                    {day || "All"}
                  </label>
                </div>
              ))}

              <hr className="mt-1" />
              <div className="d-flex justify-content-between mt-3">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleReset}
                >
                  Reset
                </button>
                <button
                  className="btn dropdown-btn bg-dark text-white btn-sm"
                  onClick={handleApply}
                >
                  Apply Now
                </button>
              </div>
            </div>
          </div>

          <button
            className="btn btn-outline-secondary action-button text-dark"
            onClick={() => setShowForm(true)}
            title="Add Version"
          >
            <img src={addIcon} className="icon-image" alt="Add" />
          </button>
        </div>
      </div>

      {totalEntries === 0 ? (
        <div className="text-center my-5">
          <p className="refresh-prompts ak-text-md">No entries found.</p>
        </div>
      ) : (
        <div className="not-disable">
          <div className="table-responsive">
            <table className="table v-table table-sm w-100">
              <thead>
                <tr>
                  <SortableHeader
                    column="version_number"
                    label="Version"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  <SortableHeader
                    column="version_module_name"
                    label="Module Name"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  <SortableHeader
                    column="version_changes"
                    label="Changes"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  <SortableHeader
                    column="version_release_date"
                    label="Release Date"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  <SortableHeader
                    column="updated_timestamp"
                    label="Last Updated"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  <SortableHeader
                    column="version_status"
                    label="Status"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((v) => (
                  <tr key={v.version_id}>
                    <td>{v.version_number}</td>
                    <td>{v.version_module_name || "—"}</td>
                    <td title={v.version_changes}>
                      {v.version_changes || "—"}
                    </td>
                    <td>{formatDateToUser(v.version_release_date)}</td>
                    <td>{formatDateToUserTimeZone(v.updated_timestamp)}</td>
                    <td>{v.version_status === "1" ? "Active" : "Inactive"}</td>
                    <td>
                      <button
                        className="icon-button"
                        onClick={() => handleView(v)}
                        title="View"
                      >
                        <img
                          src={viewIcon}
                          className="icon-image"
                          alt="View"
                          style={{ height: "16px", width: "18px" }}
                        />
                      </button>
                      <button
                        className="icon-button"
                        onClick={() => handleEdit(v)}
                        title="Edit"
                      >
                        <img src={editIcon} className="icon-image" alt="Edit" />
                      </button>
                      <button
                        className="icon-button"
                        onClick={() => handleChangeStatus(v)}
                        title="Change Status"
                      >
                        <img
                          src={changeStatusIcon}
                          className="icon-image"
                          alt="Change Status"
                        />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="table-footer fixed-bottom-footer d-flex justify-content-between align-items-center">
            <div className="refresh-prompts">
              Showing {showingFrom} to {showingTo} of {totalEntries} entries
            </div>
            <nav>
              <ul className="pagination mb-0">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(1)}
                  >
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(currentPage - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>
                {pageNumbers.map((num) => (
                  <li
                    key={num}
                    className={`page-item ${
                      num === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageClick(num)}
                    >
                      {num}
                    </button>
                  </li>
                ))}
                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(currentPage + 1)}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(totalPages)}
                  >
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}

      {/* Modal */}
      {showForm && (
        <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content w-50 mx-auto">
              <form onSubmit={handleFormSubmit}>
                <div className="modal-header justify-content-center position-relative">
                  <h5 className="modal-title m-0">
                    {viewMode
                      ? "View Version"
                      : editingVersionId
                        ? "Update Version"
                        : "Add Version"}
                  </h5>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={closeModal}
                    aria-label="Close"
                  >
                    <img src={crossIcon} className="icon-image" alt="Close" />
                  </button>
                </div>

                <div className="modal-body">
                  {/* Version Number & Module Name on the same row */}
                  <div className="row mb-3">
                    <div className="col">
                      <label className="form-label">Version Number</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.version_number}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            version_number: e.target.value,
                          })
                        }
                        required
                        disabled={viewMode}
                      />
                    </div>
                    <div className="col">
                      <label className="form-label">Module Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.version_module_name}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            version_module_name: e.target.value,
                          })
                        }
                        disabled={viewMode}
                      />
                    </div>
                  </div>

                  {/* Release Date with DatePicker */}
                  <div className="mb-3">
                    <label className="form-label">Release Date</label>
                    <DatePicker
                      selected={
                        formData.version_release_date
                          ? new Date(formData.version_release_date)
                          : null
                      }
                      onChange={(date) => {
                        const iso = date?.toISOString().split("T")[0];
                        setFormData((prev) => ({
                          ...prev,
                          version_release_date: iso,
                        }));
                      }}
                      dateFormat="MM/dd/yyyy"
                      customInput={<CustomDateInput />}
                      disabled={viewMode}
                    />
                  </div>

                  {/* Changes textarea */}
                  <div className="mb-3">
                    <label className="form-label">Changes</label>
                    <textarea
                      className="input-textarea1"
                      value={formData.version_changes}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          version_changes: e.target.value,
                        })
                      }
                      disabled={viewMode}
                    />
                  </div>
                </div>

                <div className="modal-footer">
                  {!viewMode && (
                    <button
                      type="submit"
                      className="px-5 rounded custom-btn border-none py-2 fw-bold"
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? editingVersionId
                          ? "Updating..."
                          : "Adding..."
                        : editingVersionId
                          ? "Update Version"
                          : "Add Version"}
                    </button>
                  )}
                  {viewMode && (
                    <button
                      type="button"
                      className="cancle-btn"
                      onClick={closeModal}
                    >
                      Close
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VersionHistory;
